import { useCallback } from "react";
import { useSelector } from "react-redux";

import {
  InvitationActionSourceType,
  useBandContext,
  useBandInvitationContext,
  usePermissionsContext,
} from "Bands/contexts";
import { BAND_PATHS } from "Bands/router";
import { pendingBandInvitationsSelector } from "Bands/state";
import { BAND_ACTIONS } from "Bands/types";
import {
  getPendingInvitationsActions,
  useBandRoute,
  useGetMemberTableColumns,
  usePagination,
} from "Bands/utils";
import { copy } from "shared/assets";
import { Stop } from "shared/assets/icons";
import { Card, IActionDropdownItem, Pager, Table } from "shared/components";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";
import { getIdFromPath } from "shared/utils";

const {
  members: { pendingIvitationsTable: tableCopy },
} = copy;

export const PendingInvitationsTable = () => {
  const { allBandUsers } = useBandContext();
  const { userCanManageMembers } = usePermissionsContext();

  const invitations = useSelector(pendingBandInvitationsSelector);
  const pagination = usePagination(BAND_ACTIONS.getPendingBandInvitations);
  const { openSendModal, openDeleteModal, fetchPendingInvitations, setInvitationActionSource } =
    useBandInvitationContext();
  const { pushRoute } = useBandRoute();

  const getRowActions = useCallback(
    (invitationId: string): IActionDropdownItem[] => {
      const invitation = invitations.find(({ id }) => invitationId === id);

      if (!invitation) return [];

      if (!userCanManageMembers) return [];

      const bandUser = allBandUsers.find(
        ({ invitation: inv }) => inv && inv.id === API_ENDPOINTS.BAND_INVITATION(invitationId)
      );

      if (!bandUser) return [];

      const actions = getPendingInvitationsActions({
        handleSend: () => openSendModal(invitation, InvitationActionSourceType.pending),
        handleEdit: () => {
          setInvitationActionSource(InvitationActionSourceType.pending);

          pushRoute(BAND_PATHS.EDIT_INVITATION, {
            params: { invitationId },
          });
        },
        handleEditPermissions: () =>
          pushRoute(BAND_PATHS.EDIT_PERMISSION, {
            params: {
              userId: getIdFromPath(bandUser.id, API_ENDPOINTS.BAND_USERS) || "",
            },
          }),
        handleDelete: () => openDeleteModal(invitation, InvitationActionSourceType.pending),
      });

      return actions;
    },
    [
      invitations,
      userCanManageMembers,
      allBandUsers,
      openSendModal,
      setInvitationActionSource,
      pushRoute,
      openDeleteModal,
    ]
  );

  const { columns } = useGetMemberTableColumns(invitations[0]);

  if (!invitations.length) return null;

  return (
    <>
      <Card title={tableCopy.title} icon={Stop}>
        <Spacer size="s">
          <Table
            data={invitations}
            columns={columns}
            getRowActions={getRowActions}
            dataTestId="pending_invitations_table"
            options={{
              showActions: userCanManageMembers,
            }}
          />
          <Pager {...pagination} onPageChange={fetchPendingInvitations} />
        </Spacer>
      </Card>
    </>
  );
};
